import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveWbsConfiguration,
} from "@planzone/view-models";
import { Safeguard, useRoles } from "@scenes/safeguard";
import { useModal } from "@root/client/modal";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { Language } from "@common/language";
import { Controls } from "@common/treeToggle";
import { Props } from "./types";
import styles from "./styles.scss";

const fixedColumns = [
  "statuses",
  "tags",
  "allocations",
  "allocated",
  "spent",
  "startDate",
  "endDate",
  "completionDate",
];

function Heading({ project }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { configuration } = useRetrieveWbsConfiguration(planzone);
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project.uuid,
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const { pushView } = useModal();
  const roles = useRoles({ project });
  const profile =
    configuration.find((p) =>
      p.roles.find((role) =>
        roles.find((r) => r.uuid === role || r.name === role),
      ),
    ) || configuration.length > 0
      ? configuration[0]
      : {
          name: "",
          roles: [],
          columns: [],
        };
  const columns = profile.columns.filter(
    ({ key }) =>
      !![...fixedColumns, ...template.map(({ uuid }) => uuid)].find(
        (criteria) => criteria === key,
      ),
  );
  const colStyle = {
    width: 0,
    minWidth: "9rem",
  };

  return (
    <Safeguard useCases={[UseCases.editWbsConfiguration]}>
      {({ canEditWbsConfiguration }) => (
        <div className={styles.heading}>
          <Language>
            {({ currentLanguage }) => (
              <React.Fragment>
                <div className={styles.row}>
                  <Controls />
                  <div className={styles.columns}>
                    {columns.map(({ key }) => {
                      const criteria = template.find(
                        ({ uuid }) => uuid === key,
                      );

                      return (
                        <p key={key} className={styles.label} style={colStyle}>
                          {criteria
                            ? criteria.labels[
                                currentLanguage as keyof typeof criteria.labels
                              ]
                              ? criteria.labels[
                                  currentLanguage as keyof typeof criteria.labels
                                ]
                              : criteria.name
                            : t(`wbs.columnNames.${key}`, key)}
                        </p>
                      );
                    })}
                  </div>
                  <p className={styles.label} style={colStyle} />
                </div>
                <div className={styles.contextualMenu}>
                  {canEditWbsConfiguration ? (
                    <div
                      className={styles.contextualMenuIcon}
                      onClick={() =>
                        pushView({
                          key: UseCases.retrieveWbsConfiguration,
                          data: { projectId: project.uuid },
                        })
                      }
                    >
                      <CogWheelIcon className={styles.wbsConfigurationIcon} />
                    </div>
                  ) : (
                    <div className={styles.contextualMenuIcon} />
                  )}
                </div>
              </React.Fragment>
            )}
          </Language>
        </div>
      )}
    </Safeguard>
  );
}

export { Heading };
